import { t } from '@lingui/core/macro';
import { Drawer, DrawerProps } from '@mui/material';
import { ErrorBoundary, FallbackRender } from '@sentry/react';
import React from 'react';

import { CenteredSpinner } from '../CircularProgress';
import { CenteredLayout } from '../layout/CenteredLayout';
import { NonIdealState } from '../NonIdealState';

type RightBoxDrawerProps = Omit<DrawerProps, 'onClose' | 'open'> & {
    onClose: () => void;
    FallbackComponent?: FallbackRender;
};

const Fallback: React.FunctionComponent = () => {
    return (
        <CenteredSpinner
            sx={{
                width: '400px',
                height: '100%',
            }}
        />
    );
};

const ErrorFallback: FallbackRender = ({ error }) => {
    const errorMessage =
        error instanceof Error ? error.message : t`An unexpected error occurred. Please try again or reload the page.`;

    return (
        <CenteredLayout
            sx={{
                width: '400px',
                height: '100%',
            }}
        >
            <NonIdealState variant="error" title="Error" description={errorMessage} />
        </CenteredLayout>
    );
};

type PaperSlotProps = NonNullable<NonNullable<DrawerProps['slotProps']>['paper']>;
type DrawerOwnerState = Parameters<Extract<PaperSlotProps, (...args: never[]) => unknown>>[0];

const PAPER_SHADOW = '-4px 0px 20px 0px rgba(42, 49, 71, 0.1)';

/** The caller's paper props, such as an `aria-label` for the drawer's dialog, over the drawer's shadow. */
function withPaperShadow(paper: PaperSlotProps | undefined): PaperSlotProps {
    return (ownerState: DrawerOwnerState) => {
        const callerProps = typeof paper === 'function' ? paper(ownerState) : paper;
        return { ...callerProps, style: { boxShadow: PAPER_SHADOW, ...callerProps?.style } };
    };
}

export const RightBoxDrawer = React.forwardRef<unknown, RightBoxDrawerProps>(
    ({ onClose, children, FallbackComponent, slotProps, ...props }, _) => {
        return (
            <Drawer
                anchor={'right'}
                open={true}
                onClose={onClose}
                ModalProps={{ slotProps: { backdrop: { invisible: true } } }}
                // The stop propagation below is required => otherwise the onClick handler of component containing the drawer is triggered.
                onClick={(event): void => event.stopPropagation()}
                {...props}
                slotProps={{ ...slotProps, paper: withPaperShadow(slotProps?.paper) }}
            >
                <ErrorBoundary fallback={FallbackComponent ?? ErrorFallback}>
                    <React.Suspense fallback={<Fallback />}>{children}</React.Suspense>
                </ErrorBoundary>
            </Drawer>
        );
    },
);
